// WHICH TONE CURVE THE FRAME IS RENDERED THROUGH, and why it is a property of
// WHAT IS IN THE FRAME rather than a house style.
//
// ACES is a SCENE-REFERRED to DISPLAY-REFERRED transform: it takes radiance a
// renderer computed and fits it to a screen. A GLB character is exactly that —
// PBR materials lit by probes and a sun — and wants it.
//
// A SPLAT IS ALREADY DISPLAY-REFERRED. Its colour is what the capture looked
// like on a screen; the material takes it into working space only so it can
// BLEND (`GaussianSplatPatched`), and the output pass encodes it back. Running
// ACES over that is applying a display transform to something that has already
// had one, and it is not subtle: measured on isaac-v8's face, +10.9 mean luma
// over a 280x400 crop, per-channel mean |diff| 14.8 and max 34.
//
// It became visible when the splats moved into the app's scene. They used to be
// drawn by the bridge's OWN renderer, which never set `toneMapping` — three's
// default is `NoToneMapping` — so the avatar reached the screen untouched while
// the app canvas underneath ran ACES. One renderer means one answer, and this is
// where it is chosen.
//
// THERE IS NO PER-OBJECT ANSWER, and both ways out were tried and measured
// (`GaussianSplatPatched`'s note): `three.webgpu.js` contains ZERO references to
// `toneMapped`, and in the node renderer the curve is a FRAME-LEVEL output pass
// (`NodeManager` -> `.renderOutput( renderer.toneMapping, … )`), applied after
// everything has blended. So the question can only be answered per frame.
//
// WHAT THIS DOES NOT FIX: a GLB character wearing a splat ROOM. That frame holds
// both kinds at once, the PBR skin needs the curve and the room does not, and
// one pass cannot serve both — so the room keeps being tone-mapped there, which
// is what `ThreeEnvironmentSplat`'s header already documents as a known
// difference from the renderer before it. It is not made worse here, and it is
// not fixable without giving the splats their own pass.
//
// Note the readback is unaffected either way: `Renderer.js` returns
// `NoToneMapping` for anything that is not the output target, so `__READ_PIXEL__`
// and the IBL bake have always seen the untone-mapped frame — which is what a
// bake wants, and which is also why a readback cannot be used to measure this.

import { ACESFilmicToneMapping, NoToneMapping } from 'three'

/**
 * @param {object} frame
 * @param {boolean} frame.splatCharacter  the avatar is Gaussian splats, so
 *   nothing in the frame is scene-referred: no PBR meshes, and its room (when it
 *   has one) is splats in the same mesh.
 * @returns {number} a THREE tone-mapping constant
 */
export function toneMappingFor({ splatCharacter } = {}) {
  return splatCharacter ? NoToneMapping : ACESFilmicToneMapping
}
